import { API_BASE_URL } from '../config.ts';

export interface ApiErrorDetail {
  field: string;
  message: string;
}

/**
 * 404 que NO ocultan nada: hablan de algo que el usuario acaba de escribir y decírselo
 * es lo que le permite corregirlo. Ver `ApiError.isRestricted`.
 */
const CODIGOS_404_INFORMATIVOS = new Set(['invite_code_not_found']);

/**
 * Error de la API con el `code` estable del backend. Ese código es lo que se traduce
 * (errors.<code> en los archivos de traducción); el `message` del servidor viene en
 * español y NO se enseña al usuario, solo sirve para depurar.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly serverMessage: string,
    readonly details: ApiErrorDetail[] = [],
  ) {
    super(`${status} ${code}: ${serverMessage}`);
    this.name = 'ApiError';
  }

  /**
   * 404 de privacidad. El backend responde 404 —no 403— a una liga de la que no eres
   * miembro, para no confirmar siquiera que existe (ADR 0004). Desde el cliente los dos
   * casos son indistinguibles **por diseño**, así que la interfaz no debe intentar
   * adivinar cuál es: enseña un estado propio, ni un error genérico ni un "no tienes
   * permiso" que ya sería filtrar la información que el backend se calla.
   *
   * No TODO 404 es de privacidad. `invite_code_not_found` responde a un código que el
   * usuario acaba de teclear: ahí lo útil es decir "ese código no existe" y dejarle
   * corregir la errata. El backend ya decidió distinguirlo dándole su propio código de
   * error, y esconderlo detrás de "contenido no accesible" solo confundiría.
   *
   * La lista es de EXCEPCIONES, no de casos incluidos: un 404 nuevo que nadie haya
   * clasificado se trata como privado, que es el lado seguro por el que equivocarse.
   */
  get isRestricted(): boolean {
    return this.status === 404 && !CODIGOS_404_INFORMATIVOS.has(this.code);
  }
}

/** Fallo de red: no hubo respuesta. Distinto de una respuesta de error del servidor. */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('No se pudo contactar con el servidor');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

type UnauthorizedHandler = () => void;

let onUnauthorized: UnauthorizedHandler = () => {};

/**
 * Lo registra la aplicación al arrancar. Se hace así, y no importando el router aquí,
 * para que el cliente de API no dependa del enrutado ni haya un ciclo entre módulos.
 */
export function setUnauthorizedHandler(handler: UnauthorizedHandler): void {
  onUnauthorized = handler;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /**
   * Desactiva la reacción global al 401.
   *
   * Hace falta en exactamente dos sitios y conviene entender por qué:
   *   - `POST /auth/login`, que responde 401 cuando las credenciales son incorrectas.
   *     Sin esto, fallar el login te echaría a la pantalla de login desde la pantalla
   *     de login, y el mensaje de error se perdería por el camino.
   *   - `GET /auth/me`, que responde 401 cuando simplemente no hay sesión. Eso no es un
   *     error: es la respuesta a "¿hay alguien?" y quien decide a dónde ir es el router.
   */
  skipAuthRedirect?: boolean;
}

/**
 * Cliente HTTP único de la aplicación.
 *
 * `credentials: 'include'` en TODAS las peticiones: la sesión va en una cookie httpOnly
 * que el JavaScript no puede leer ni adjuntar a mano (ADR 0003). Sin esta opción el
 * navegador no la manda y todo responde 401.
 */
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, skipAuthRedirect = false } = options;

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      credentials: 'include',
      headers: body === undefined ? { Accept: 'application/json' } : {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: body === undefined ? null : JSON.stringify(body),
      ...(signal ? { signal } : {}),
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new NetworkError(cause);
  }

  if (response.status === 401 && !skipAuthRedirect) {
    // Sesión caducada o revocada en el servidor. Se avisa una sola vez y desde aquí,
    // para que ninguna pantalla tenga que acordarse de comprobarlo.
    onUnauthorized();
  }

  if (response.status === 204) return undefined as T;

  const payload = await readJson(response);

  if (!response.ok) {
    const error = (payload as { error?: { code?: string; message?: string; details?: ApiErrorDetail[] } })
      ?.error;
    throw new ApiError(
      response.status,
      error?.code ?? 'unexpected',
      error?.message ?? response.statusText,
      error?.details ?? [],
    );
  }

  return payload as T;
}

async function readJson(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text.length === 0) return null;
  try {
    return JSON.parse(text);
  } catch {
    // Una respuesta que no es JSON con un estado de error suele ser el proxy o nginx,
    // no el backend. Se trata como inesperada en vez de romper el parseo.
    return null;
  }
}
